/**
 * A predicate engine for filtering records across arbitrary fields.
 *
 * The DSL shares its operator vocabulary and boolean recursion with the tag predicate system via
 * `@storyboard/predicates`. Its one extension is a `field` dimension: each value leaf targets one column of
 * the flattened virtual driver table through a {@link FieldRef} strategy object and evaluates against a
 * whole record rather than a single string. `isTrue`/`isFalse` are fieldless constants (used by the
 * match-all / match-none sentinels). The DSL serializes to plain JSON so a query can be copied out, saved,
 * and pasted back in — field references carry natural keys (e.g. a tag's slug), never database ids, so a
 * serialized query is portable across environments.
 */

import { z } from "zod";
import { fromError } from "zod-validation-error";
import type { LeafEvaluator, PredicateObject, RadiusSpec } from "@storyboard/predicates/evaluate";
import { evaluateNode, evaluateScalar } from "@storyboard/predicates/evaluate";
import {
  combinatorSchemas,
  eqShape,
  gtShape,
  inArrayShape,
  isEmptyShape,
  isFalseShape,
  isTrueShape,
  ltShape,
  newerThanShape,
  olderThanShape,
  regexShape,
  strictLeaf,
  withinRadiusShape,
} from "@storyboard/predicates/schema";

/**
 * A strategy object naming one column of the flattened virtual driver table.
 *
 * Discriminated on `strategy` so new column sources can be added without a DSL migration:
 * - `tag` — a workspace tag column, referenced by its immutable slug (never its database id).
 * - `eld` — a raw ELD snapshot column, referenced by table and column name.
 * - `core` — a Storyboard-side concept (e.g. `vendor_id`, `member_name`), referenced by kind.
 *
 * A ref only labels/resolves a column of the already-flattened table; it does not drive how the
 * underlying data is sourced.
 */
export type FieldRef =
  | { strategy: "tag"; slug: string }
  | { strategy: "eld"; table: string; column: string }
  | { strategy: "core"; kind: string };

/**
 * Builds a tag column reference from the tag's immutable slug.
 */
export function tagFieldRef(slug: string): FieldRef {
  return { strategy: "tag", slug };
}

/**
 * Builds a raw ELD snapshot column reference.
 */
export function eldFieldRef(table: string, column: string): FieldRef {
  return { strategy: "eld", table, column };
}

/**
 * Builds a Storyboard-side (core) concept reference.
 */
export function coreFieldRef(kind: string): FieldRef {
  return { strategy: "core", kind };
}

/** The flattened row's linking identifier (the driver's ELD external id / membership link vendor id). */
export const VENDOR_ID_FIELD: FieldRef = coreFieldRef("vendor_id");

/** The aggregate "any tag" field: every tag value on the row, matched per value. */
export const ANY_TAG_FIELD: FieldRef = coreFieldRef("any_tag");

/**
 * The canonical string key for a field reference, used as the record/column key on the flattened table.
 *
 * One deterministic serialization per ref so predicates, column defs, and projected records always agree.
 */
export function fieldKey(ref: FieldRef): string {
  switch (ref.strategy) {
    case "tag":
      return `tag:${ref.slug}`;
    case "eld":
      return `eld:${ref.table}.${ref.column}`;
    case "core":
      return `core:${ref.kind}`;
  }
}

/**
 * The field reference a canonical key names: the inverse of {@link fieldKey}, or `null` for a string that is not one.
 *
 * Documents that lay in the config cascade spell a field as its key rather than as the object, so two layers never
 * compose one; this is how they recover the reference where a predicate leaf needs it.
 *
 * TODO - WEB-2283: a field should have one spelling. Once a leaf's `field` is the key itself, this function and
 * `fieldKey` become the identity and go away.
 */
export function parseFieldKey(key: string): FieldRef | null {
  if (key.startsWith("tag:")) {
    const slug = key.slice("tag:".length);
    return slug === "" ? null : tagFieldRef(slug);
  }
  if (key.startsWith("core:")) {
    const kind = key.slice("core:".length);
    return kind === "" ? null : coreFieldRef(kind);
  }
  if (key.startsWith("eld:")) {
    const rest = key.slice("eld:".length);
    const dot = rest.indexOf(".");
    if (dot <= 0 || dot === rest.length - 1) {
      return null;
    }
    return eldFieldRef(rest.slice(0, dot), rest.slice(dot + 1));
  }
  return null;
}

/**
 * A compiled-at-eval regular expression spec; kept as strings so the node round-trips through JSON.
 */
export type RegexSpec = { pattern: string; flags?: string };

export type { RadiusSpec };

/**
 * A single condition testing one field's value.
 */
export type LeafNode =
  | { field: FieldRef; eq: string }
  | { field: FieldRef; inArray: string[] }
  | { field: FieldRef; regex: RegexSpec }
  | { field: FieldRef; isEmpty: Record<string, never> }
  /** The field's ISO timestamp is more than `minutes` before the evaluation instant. */
  | { field: FieldRef; olderThan: { minutes: number } }
  /** The field's ISO timestamp is within the last `minutes` of the evaluation instant. */
  | { field: FieldRef; newerThan: { minutes: number } }
  /** The field parses as a number greater than the comparand. */
  | { field: FieldRef; gt: number }
  /** The field parses as a number less than the comparand. */
  | { field: FieldRef; lt: number }
  /** The field, a `"latitude,longitude"` pair, lies within the radius of the center. */
  | { field: FieldRef; withinRadius: RadiusSpec };

/**
 * A fieldless constant predicate (used by the match-all / match-none sentinels).
 */
export type ConstantNode = { isTrue: Record<string, never> } | { isFalse: Record<string, never> };

/**
 * A leaf condition, a fieldless constant, or a boolean combination of nodes.
 */
export type FilterNode = LeafNode | ConstantNode | { and: FilterNode[] } | { or: FilterNode[] } | { not: FilterNode };

/** The field-targeting leaf operators, in builder display order. */
export const FILTER_OPERATORS = [
  "eq",
  "inArray",
  "regex",
  "isEmpty",
  "olderThan",
  "newerThan",
  "gt",
  "lt",
  "withinRadius",
] as const;
export type FilterOperator = (typeof FILTER_OPERATORS)[number];

/**
 * The result of parsing serialized filter text; `node` is `null` for an empty (no-op) filter.
 */
export type ParseResult = { ok: true; node: FilterNode | null } | { ok: false; error: string };

/**
 * The Zod schema behind {@link FieldRef}; shared with the Dispatch view schema so the two never drift.
 *
 * Named `FieldRef` on the wire: an OpenAPI document that embeds it emits one component and references it.
 */
export const fieldRefSchema = z
  .discriminatedUnion("strategy", [
    z.object({ strategy: z.literal("tag"), slug: z.string().min(1) }).strict(),
    z.object({ strategy: z.literal("eld"), table: z.string().min(1), column: z.string().min(1) }).strict(),
    z.object({ strategy: z.literal("core"), kind: z.string().min(1) }).strict(),
  ])
  .meta({ id: "FieldRef" });

/** The `field` key mixed into every value leaf: a strategy object naming the targeted column. */
const fieldShape = { field: fieldRefSchema };

/** The scalar leaf union: the shared value operators with a `field`, plus the fieldless constants. */
export const scalarSchema = z.union([
  strictLeaf(eqShape, fieldShape),
  strictLeaf(inArrayShape, fieldShape),
  strictLeaf(regexShape, fieldShape),
  strictLeaf(isEmptyShape, fieldShape),
  strictLeaf(olderThanShape, fieldShape),
  strictLeaf(newerThanShape, fieldShape),
  strictLeaf(gtShape, fieldShape),
  strictLeaf(ltShape, fieldShape),
  strictLeaf(withinRadiusShape, fieldShape),
  strictLeaf(isTrueShape),
  strictLeaf(isFalseShape),
]);

/**
 * The whole filter grammar: field-targeting leaves combined with `and` / `or` / `not`, recursively.
 *
 * Exported for the schema's own tests and for the wire, where it is the component `FilterNode` that references
 * itself. Production code goes through {@link parseFilterNode} instead, which adds the depth guard a pasted query
 * needs — this schema alone would recurse as far as the input asks it to.
 */
export const filterNodeSchema = combinatorSchemas(scalarSchema, "FilterNode").any;

/**
 * The flat `field key -> value` record the predicate engine evaluates against.
 *
 * A multi-value field (the tag aggregate, or a column carrying individual `values`) projects to its
 * value list and evaluates any-of; everything else projects to its single display string.
 */
export type ProjectedRecord = Record<string, string | string[]>;

/**
 * Evaluates a filter node against a record projected to `field key → value`.
 *
 * Each leaf's {@link FieldRef} resolves to its canonical {@link fieldKey}; missing fields read as the
 * empty string. `eq` and `inArray` are exact; `regex` compiles per call; `isTrue`/`isFalse` ignore the
 * record. All leaf semantics come from `@storyboard/predicates`.
 *
 * A multi-value field (projected as a value list) evaluates any-of: the leaf passes when any single
 * value passes, so `eq "A"` matches a driver tagged both A and B. `isEmpty` is the exception — it asks
 * about the collection, so it passes only when the field has no (non-blank) values at all.
 *
 * @param node - The filter to evaluate.
 * @param record - The record projected to a flat map of field key to its value(s).
 * @param now - The evaluation instant in epoch ms for `olderThan`/`newerThan`; defaults to the wall clock.
 */
export function evaluateFilter(node: FilterNode, record: ProjectedRecord, now: number = Date.now()): boolean {
  const evalLeaf: LeafEvaluator = (leaf) => {
    const field = (leaf as { field?: FieldRef }).field;
    const value = field ? (record[fieldKey(field)] ?? "") : "";
    if (!Array.isArray(value)) {
      return evaluateScalar(leaf, value, now);
    }
    if ("isEmpty" in leaf) {
      return value.every((entry) => entry.trim() === "");
    }
    return value.some((entry) => evaluateScalar(leaf, entry, now));
  };
  return evaluateNode(node as PredicateObject, evalLeaf);
}

/**
 * Evaluates a filter against a record, treating a `null` filter as match-everything.
 *
 * @returns whether the record passes the filter; a `null` filter matches every record.
 */
export function matchesFilter(node: FilterNode | null, record: ProjectedRecord, now: number = Date.now()): boolean {
  return node === null ? true : evaluateFilter(node, record, now);
}

/**
 * The never-match filter: `isFalse` matches no record.
 *
 * Use this as an explicit, non-null "match nothing" node where `null` would carry a context-dependent
 * meaning — e.g. the query builder's None mode for a permissions predicate, where `null` reads as
 * match-everything.
 */
export const MATCH_NONE: FilterNode = { isFalse: {} };

/**
 * The always-match filter: `isTrue` matches every record.
 *
 * Unlike a `null` filter — which call sites read as "no filter" with context-dependent meaning — this is an
 * explicit, non-null "match everything" node.
 */
export const MATCH_ALL: FilterNode = { isTrue: {} };

/**
 * Detects the {@link MATCH_NONE} sentinel.
 *
 * @returns whether the node is the canonical never-match filter (an `isFalse` leaf).
 */
export function isMatchNone(node: FilterNode | null): boolean {
  return node !== null && "isFalse" in node;
}

/**
 * Detects the {@link MATCH_ALL} sentinel.
 *
 * @returns whether the node is the canonical always-match filter (an `isTrue` leaf).
 */
export function isMatchAll(node: FilterNode | null): boolean {
  return node !== null && "isTrue" in node;
}

/**
 * Serializes a filter node to pretty JSON suitable for copy/paste and storage.
 */
export function serializeFilter(node: unknown): string {
  return JSON.stringify(node, null, 2);
}

/** Bounds recursion when parsing pasted JSON so a pathologically nested query can't blow the stack. */
const MAX_PARSE_DEPTH = 100;

/**
 * Verifies a parsed value nests no deeper than {@link MAX_PARSE_DEPTH}, guarding the recursive schema from overflow.
 */
function withinDepth(value: unknown, depth: number): boolean {
  if (depth > MAX_PARSE_DEPTH) {
    return false;
  }
  if (value !== null && typeof value === "object") {
    for (const child of Object.values(value)) {
      if (!withinDepth(child, depth + 1)) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Validates an already-parsed value into a {@link FilterNode}.
 *
 * `null`/`undefined` yields `node: null` (no filter), matching empty serialized input. Use this when the
 * value is already an object (e.g. a stored jsonb predicate) rather than serialized text.
 *
 * @param value - The already-parsed filter value (object, `null`, or `undefined`).
 */
export function parseFilterNode(value: unknown): ParseResult {
  if (value === null || value === undefined) {
    return { ok: true, node: null };
  }

  if (!withinDepth(value, 0)) {
    return { ok: false, error: `filter: nested too deeply (max ${MAX_PARSE_DEPTH} levels)` };
  }

  const result = filterNodeSchema.safeParse(value);
  if (result.success) {
    return { ok: true, node: result.data as FilterNode };
  }
  return { ok: false, error: fromError(result.error, { prefix: null }).toString() };
}

/**
 * Parses serialized filter text (JSON) into a validated {@link FilterNode}.
 *
 * Empty/whitespace input is valid and yields `node: null` (no filter).
 *
 * @param text - The serialized filter, typically pasted by the user.
 */
export function parseFilter(text: string): ParseResult {
  const trimmed = text.trim();
  if (trimmed === "") {
    return { ok: true, node: null };
  }

  let json: unknown;
  try {
    json = JSON.parse(trimmed);
  } catch (error) {
    return { ok: false, error: `Invalid JSON: ${error instanceof Error ? error.message : "unknown error"}` };
  }

  return parseFilterNode(json);
}
